import {WaypointDispatcher} from '../../../hooks/useWaypoints';
import {TrackDispatcher} from '../../../hooks/useTracks';
import normal from './types/normal';
import editingRoute from './types/editingRoute';
import movingWaypoint from './types/movingWaypoint';
import {AreaDispatcher} from '../../../hooks/useAreas';

export interface GetInteractionHandlersOptions<T = MapInteraction> {
    foldersMembership: MapFoldersMembership;
    highlight: MapHighlight;
    interaction: T;
    map: mapboxgl.Map | undefined;
    mapboxStyle: GaiaMapboxStyle | undefined;
    onClickExternal?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onDblClickExternal?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onFoldersMembershipChange: (foldersMembership: MapFoldersMembership) => void;
    onInteractionChange: SetState<MapInteraction>;
    onMouseDownExternal?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onMouseMoveExternal?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onMouseOutExternal?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onMouseUpExternal?: (event: mapboxgl.MapLayerMouseEvent) => void;
    onOverlaysChange: SetState<MapOverlays>;
    onPopupChange: (popup: MapPopup | undefined) => void;
    onSearchResultChange: (searchResult: SearchResult | undefined) => void;
    onTouchEndExternal?: (event: mapboxgl.MapTouchEvent) => void;
    onTouchMoveExternal?: (event: mapboxgl.MapTouchEvent) => void;
    onTouchStartExternal?: (event: mapboxgl.MapTouchEvent) => void;
    overlays: MapOverlays;
    popup: MapPopup;
    trackDispatcher: TrackDispatcher;
    tracks: {[pointLocalId: string]: Track};
    waypointDispatcher: WaypointDispatcher;
    waypoints: {[pointLocalId: string]: Waypoint};
    areaDispatcher: AreaDispatcher;
    areas: {[areaLocalId: string]: Area};
}

type Handler = (
    event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
    options: GetInteractionHandlersOptions<any>
) => void;

const handlers: {[type: string]: {
    getCursor: (interaction: any) => string;
    onMouseDown: Handler;
    onMouseUp: Handler;
    onMouseMove: Handler;
    onMouseOut: Handler;
    onClick: Handler;
    onDblClick: Handler;
};} = {normal, editingRoute, movingWaypoint};

export default function (options: GetInteractionHandlersOptions) {
    const {
        onClickExternal,
        onDblClickExternal,
        onMouseDownExternal,
        onMouseMoveExternal,
        onMouseUpExternal,
        onMouseOutExternal,
        onTouchEndExternal,
        onTouchMoveExternal,
        onTouchStartExternal,
        interaction,
    } = options;

    return {
        cursor: handlers[interaction.type].getCursor(options.interaction),

        onClick: (event: mapboxgl.MapLayerMouseEvent) => {
            if (onClickExternal) onClickExternal(event);
            options.onPopupChange(undefined);
            handlers[interaction.type].onClick(event, options);
        },

        onDblClick: (event: mapboxgl.MapLayerMouseEvent) => {
            if (onDblClickExternal) onDblClickExternal(event);
            handlers[interaction.type].onDblClick(event, options);
        },

        onMouseMove: (event: mapboxgl.MapLayerMouseEvent) => {
            if (onMouseMoveExternal) onMouseMoveExternal(event);
            handlers[interaction.type].onMouseMove(event, options);
        },

        onMouseDown: (event: mapboxgl.MapLayerMouseEvent) => {
            if (onMouseDownExternal) onMouseDownExternal(event);
            handlers[interaction.type].onMouseDown(event, options);
        },

        onMouseUp: (event: mapboxgl.MapLayerMouseEvent) => {
            if (onMouseUpExternal) onMouseUpExternal(event);
            handlers[interaction.type].onMouseUp(event, options);
        },

        onMouseOut: (event: mapboxgl.MapLayerMouseEvent) => {
            if (onMouseOutExternal) onMouseOutExternal(event);
            handlers[interaction.type].onMouseOut(event, options);
        },

        onTouchStart: (event: mapboxgl.MapTouchEvent) => {
            if (onTouchStartExternal) onTouchStartExternal(event);
            handlers[interaction.type].onMouseDown(event, options);
        },

        onTouchEnd: (event: mapboxgl.MapTouchEvent) => {
            if (onTouchEndExternal) onTouchEndExternal(event);
            handlers[interaction.type].onMouseUp(event, options);
        },

        onTouchMove: (event: mapboxgl.MapTouchEvent) => {
            if (onTouchMoveExternal) onTouchMoveExternal(event);
            handlers[interaction.type].onMouseMove(event, options);
        },
    };
}
